import { useMemo } from 'react'
import {
  ColumnFilter,
  ColumnFiltersState,
  SortingState,
} from '@tanstack/react-table'

type QueryBuilderMapping = {
  columnId: string
  paramName: string
}

type BaseQueryParams = {
  limit: number
  offset: number
  sortBy?: string
  sortOrder?: 'asc' | 'desc'
}

type QueryParams<T = Record<string, never>> = BaseQueryParams & T

type CustomFilterMapper<T> = (
  filter: ColumnFilter,
  params: QueryParams<T>,
) => boolean

type UseQueryBuilderQueryProps<T = Record<string, never>> = {
  supportedFilters: QueryBuilderMapping[]
  defaultFilters?: Partial<QueryParams<T>>
  customFilterMapper?: CustomFilterMapper<T>
}

export function useQueryBuilder<T = Record<string, never>>({
  supportedFilters,
  defaultFilters = {},
  customFilterMapper,
}: UseQueryBuilderQueryProps<T>) {
  const buildQueryArgs = useMemo(
    () =>
      ({
        pageIndex,
        pageSize,
        sorting,
        filters,
      }: {
        pageIndex: number
        pageSize: number
        sorting: SortingState
        filters: ColumnFiltersState
      }): QueryParams<T> => {
        const params: QueryParams<T> = {
          limit: pageSize,
          offset: pageIndex * pageSize,
          ...defaultFilters,
        } as QueryParams<T>

        if (sorting.length > 0) {
          const { id, desc } = sorting[0]
          params.sortBy = id
          params.sortOrder = desc ? 'desc' : 'asc'
        }

        filters.forEach((filter: ColumnFilter) => {
          const handledByCustomMapper = customFilterMapper
            ? customFilterMapper(filter, params)
            : false

          if (!handledByCustomMapper) {
            const filterMapping = supportedFilters.find(
              (f) => f.columnId === filter.id,
            )
            if (filterMapping) {
              ;(params as Record<string, unknown>)[filterMapping.paramName] =
                filter.value as string
            }
          }
        })

        return params
      },
    [supportedFilters, customFilterMapper, defaultFilters],
  )

  return { buildQueryArgs }
}
